import { CloseRounded } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import { Dialog } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

export interface MerchantFiltersState {
  dateFrom: string;
  dateTo: string;
}

const defaultFilters: MerchantFiltersState = {
  dateFrom: "",
  dateTo: "",
};

export default function FilterMerchants({
  openModal,
  setOpenModal,
  resetFilter,
  filters,
  onApplyFilters,
  title = "Filter Merchants",
}: {
  openModal: boolean;
  setOpenModal: (val: boolean) => void;
  resetFilter: () => void;
  filters: MerchantFiltersState;
  onApplyFilters: (f: MerchantFiltersState) => void;
  title?: string;
}) {
  const [_dateFrom, _setDateFrom] = useState<string>(filters.dateFrom);
  const [_dateTo, _setDateTo] = useState<string>(filters.dateTo);

  React.useEffect(() => {
    if (openModal) {
      _setDateFrom(filters.dateFrom);
      _setDateTo(filters.dateTo);
    }
  }, [openModal, filters.dateFrom, filters.dateTo]);

  const handleApply = () => {
    onApplyFilters({
      dateFrom: _dateFrom,
      dateTo: _dateTo,
    });
    setOpenModal(false);
  };

  const handleReset = () => {
    _setDateFrom("");
    _setDateTo("");
    resetFilter();
    setOpenModal(false);
  };

  return (
    <Dialog open={openModal} onClose={() => setOpenModal(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 400, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            {title}
          </Typography>
          <IconButton onClick={() => setOpenModal(false)} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          <Typography variant="body2" fontWeight={600} color={black} mb={1}>
            Date Range
          </Typography>
          <Box display="flex" gap={1.5}>
            <DatePicker
              label="From Date"
              format="DD-MM-YYYY"
              disableFuture
              value={_dateFrom ? dayjs(_dateFrom, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setDateFrom(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ desktopPaper: { elevation: 3 }, textField: { size: "small" } }}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
            <DatePicker
              label="To Date"
              format="DD-MM-YYYY"
              disableFuture
              value={_dateTo ? dayjs(_dateTo, "YYYY-MM-DD") : null}
              onChange={(value: Dayjs | null) => {
                _setDateTo(value?.format("YYYY-MM-DD") || "");
              }}
              slotProps={{ desktopPaper: { elevation: 3 }, textField: { size: "small" } }}
              minDate={_dateFrom ? dayjs(_dateFrom, "YYYY-MM-DD") : undefined}
              sx={{ "& .MuiSvgIcon-root": { fill: purple } }}
            />
          </Box>

          <Box display="flex" gap={1.5} mt={4}>
            <Button
              fullWidth
              onClick={() => setOpenModal(false)}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export { defaultFilters };
